import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, filter, map, of, switchMap, tap } from 'rxjs';
import { OrdersActions } from './orders.actions';
import { OrdersApiService } from '../services/orders-api.service';
import { OrderState } from '@us/web/shared/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MessageService } from '@us/web/shared/feature';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EntityType, MessagingService } from '@us/web/notifications/data-access';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { Store } from '@ngrx/store';
import { getErrorMessage, getOrderPaymentQrCode, getOrderPaymentUrl } from '@us/web/shared/utils';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { QrPaymentComponent } from '@us/web/orders/ui';
import { CartActions } from '@us/web/cart/data-access';

@Injectable()
export class OrdersEffects {
    private readonly actions$ = inject(Actions);
    private readonly orderService = inject(OrdersApiService);
    private readonly messageService = inject(MessageService);
    private readonly messagingService = inject(MessagingService);
    private readonly store = inject(Store);
    private readonly dialogService = inject(TuiDialogService);
    private readonly injector = inject(Injector);

    notifications$ = createEffect(
        () => {
            return this.messagingService.notifications$.pipe(
                filter(tuiIsPresent),
                filter(value => value.entity_type === EntityType.Order),
                tap(value => {
                    this.store.dispatch(OrdersActions.loadOrder({ id: value.entity_id }));
                })
            );
        },
        { dispatch: false }
    );

    loadOrders$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrders),
            switchMap(() =>
                this.orderService.getOrders().pipe(
                    map(orders => OrdersActions.loadOrdersSuccess({ orders })),
                    catchError(error => {
                        this.messageService.error(getErrorMessage(error));
                        return of(OrdersActions.loadOrdersFailure({ error: getErrorMessage(error) }));
                    })
                )
            )
        );
    });

    loadOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrder),
            concatMap(({ type, onSuccess, onFailure, ...params }) => {
                return this.orderService.getOrder(params).pipe(
                    map(order => {
                        onSuccess?.();
                        return OrdersActions.loadOrderSuccess({ order });
                    }),
                    catchError(error => {
                        onFailure?.();
                        return of(OrdersActions.loadOrderFailed({ error: getErrorMessage(error), orderId: params.id }));
                    })
                );
            })
        );
    });

    loadOrderPaymentStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrderPaymentStatus),
            concatMap(({ id }) => {
                return this.orderService.getTransactionStatus(id).pipe(
                    map(response => OrdersActions.loadOrderSuccess({ order: response })),
                    catchError(error => {
                        this.messageService.error(getErrorMessage(error));
                        return of(OrdersActions.loadOrderFailed({ error: getErrorMessage(error), orderId: id }));
                    })
                );
            })
        );
    });

    loadOrderSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OrdersActions.loadOrderSuccess),
                tap(({ title, body }) => {
                    if (title && body) {
                        this.messageService.info(body, title);
                    }
                }),
                tap(({ order }) => {
                    if (order.current_state === OrderState.Complete || order.current_state === OrderState.Merged) {
                        this.store.dispatch(OrdersActions.completeOrder({ order }));
                        this.store.dispatch(CartActions.clearCart({}));
                    }
                })
            );
        },
        { dispatch: false }
    );

    updatePaymentMethod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.updatePaymentMethod),
            concatMap(({ orderId, paymentType, payment_detail }) => {
                return this.orderService.updatePaymentMethod(orderId, paymentType, payment_detail).pipe(
                    map(response => OrdersActions.updatePaymentMethodSuccess({ order: response })),
                    catchError(error => {
                        this.messageService.error(getErrorMessage(error));
                        return of(OrdersActions.updatePaymentMethodFailure({ error: getErrorMessage(error) }));
                    })
                );
            })
        );
    });

    updatePaymentMethodSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OrdersActions.updatePaymentMethodSuccess),
                tap(({ order }) => {
                    const paymentUrl: string | null = getOrderPaymentUrl(order);
                    if (getOrderPaymentQrCode(order)) {
                        this.dialogService.open(new PolymorpheusComponent(QrPaymentComponent, this.injector), {
                            size: 's',
                            data: {
                                order,
                                payment_detail: order.payment_detail
                            },
                            closeable: false,
                            dismissible: false
                      }).subscribe();
                      return;
                    }
                    if (paymentUrl !== null) {
                        window.location.href = paymentUrl;
                        return;
                    }
                })
            );
        },
        { dispatch: false }
    );
}
